import { createSlice } from "@reduxjs/toolkit";
import {
  getParticipantDetails,
  getParticipantDonations,
  getParticipantPhotos,
  getParticipantVideos,
  getShareDetails,
} from "../actions/participants.actions";

type ParticipantOption = { id: number; name: string };

// Map a media group of the API ({ id, participant_number, name, images | videos })
// to the format used by ParticipantMediaCollapse / PreviewMedia
const toMediaGroups = (payload: any, mediaField: "images" | "videos") =>
  ((payload?.data ?? []) as any[]).map((group) => ({
    id: group?.id,
    participant_number: group?.participant_number || "",
    name: group?.name?.trim() ?? "",
    items: ((group?.[mediaField] ?? []) as any[]).map((media, index) => {
      const url = typeof media === "string" ? media : media?.link;
      return {
        id: index,
        name: group?.name ?? "",
        sequence: index,
        ...(mediaField === "images"
          ? { attachment_ids: url }
          : { video_url: url }),
      };
    }),
  }));

// Keep every participant already seen so the select options don't
// shrink to one item once a participant is selected
const mergeParticipants = (participants: ParticipantOption[], payload: any) => {
  ((payload?.data ?? []) as any[]).forEach((group) => {
    if (group?.id && !participants.some((p) => p.id === group.id)) {
      participants.push({ id: group.id, name: group?.name?.trim() ?? "" });
    }
  });
};

const initialState = {
  participantDetails: [] as any,
  shareDetails: [] as any,
  shareTotalPages: 0 as number,
  shareParticipants: [] as ParticipantOption[],
  photos: [] as any,
  photosTotalPages: 0 as number,
  photoParticipants: [] as ParticipantOption[],
  videos: [] as any,
  videosTotalPages: 0 as number,
  videoParticipants: [] as ParticipantOption[],
  donations: [] as any,
  donationsTotalPages: 0 as number,
  // Participants found in the donations, used by the name filter
  donationParticipants: [] as ParticipantOption[],
  loading: false,
  error: null as unknown | null,
};

export const participantsSlice = createSlice({
  name: "participants",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getParticipantDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getParticipantDetails.fulfilled, (state, action) => {
      state.participantDetails = action.payload;
      state.loading = false;
    });
    builder.addCase(getParticipantDetails.rejected, (state, action) => {
      state.participantDetails = [];
      state.loading = false;
      state.error = action.payload;
    });
     builder.addCase(getShareDetails.pending, (state) => {
       state.loading = true;
     });
     builder.addCase(getShareDetails.fulfilled, (state, action) => {
       state.shareDetails = (((action.payload as any)?.data ?? []) as any[]).map(
         (group) => ({
           id: group?.id,
           participant_number: group?.participant_number || "",
           name: group?.name?.trim() ?? "",
           shares: group?.shares ?? [],
         })
       );
       mergeParticipants(state.shareParticipants, action.payload);
       state.shareTotalPages = (action.payload as any)?.total_pages ?? 0;
       state.loading = false;
     });
     builder.addCase(getShareDetails.rejected, (state, action) => {
       state.shareDetails = [];
       state.shareTotalPages = 0;
       state.loading = false;
       state.error = action.payload;
     });
     builder.addCase(getParticipantPhotos.pending, (state) => {
       state.loading = true;
     });
     builder.addCase(getParticipantPhotos.fulfilled, (state, action) => {
       state.photos = toMediaGroups(action.payload, "images");
       mergeParticipants(state.photoParticipants, action.payload);
       state.photosTotalPages = (action.payload as any)?.total_pages ?? 0;
       state.loading = false;
     });
     builder.addCase(getParticipantPhotos.rejected, (state, action) => {
       state.photos = [];
       state.photosTotalPages = 0;
       state.loading = false;
       state.error = action.payload;
     });
     builder.addCase(getParticipantVideos.pending, (state) => {
       state.loading = true;
     });
     builder.addCase(getParticipantVideos.fulfilled, (state, action) => {
       state.videos = toMediaGroups(action.payload, "videos");
       mergeParticipants(state.videoParticipants, action.payload);
       state.videosTotalPages = (action.payload as any)?.total_pages ?? 0;
       state.loading = false;
     });
     builder.addCase(getParticipantVideos.rejected, (state, action) => {
       state.videos = [];
       state.videosTotalPages = 0;
       state.loading = false;
       state.error = action.payload;
     });
     builder.addCase(getParticipantDonations.pending, (state) => {
       state.loading = true;
     });
     builder.addCase(getParticipantDonations.fulfilled, (state, action) => {
       const groups = ((action.payload as any)?.data ?? []) as any[];
       state.donations = groups.map((group) => ({
         id: group?.id,
         participant_number: group?.participant_number || "",
         name: group?.name?.trim() ?? "",
         donations: group?.donations ?? [],
       }));
       mergeParticipants(state.donationParticipants, action.payload);
       state.donationsTotalPages = (action.payload as any)?.total_pages ?? 0;
       state.loading = false;
     });
     builder.addCase(getParticipantDonations.rejected, (state, action) => {
       state.donations = [];
       state.donationsTotalPages = 0;
       state.loading = false;
       state.error = action.payload;
     });
  },
});
export default participantsSlice.reducer;
